import { useState } from "react";
import { toast } from "sonner";
import { Receipt, GoogleLogo } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT, LangToggle } from "../i18n";

export default function AuthPage() {
  const { setUser } = useAuth();
  const { t, lang } = useT();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, lang });
        if (data.email_sent) {
          setResetLink("");
          setEmailSent(true);
          toast.success(t("toast_resetSent"));
        } else if (data.reset_token) {
          setEmailSent(false);
          setResetLink(`${window.location.origin}/reset-password?token=${data.reset_token}`);
          toast.success(t("toast_resetGenerated"));
        } else {
          toast.success(t("toast_resetGeneric"));
        }
      } else {
        const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: form.email, password: form.password } : form);
        setUser(data);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-pro">
      <div className="hidden lg:flex w-1/2 bg-pro-dark flex-col justify-between p-12">
        <div className="flex items-center gap-2 text-white">
          <Receipt size={26} weight="bold" />
          <span className="font-heading font-bold text-lg tracking-tight">Pellku</span>
        </div>
        <div className="space-y-6">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
            {t("hero_title1")}<br />{t("hero_title2")}
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed max-w-md">
            {t("brand_sub")}
          </p>
          <div className="font-mono text-xs text-slate-500 space-y-1.5 border-l border-slate-700 pl-4">
            <p>→ AI extraction · Gemini vision</p>
            <p>→ Draft / Submitted / Approved / Reimbursed</p>
            <p>→ QuickBooks · Xero · CSV export</p>
          </div>
        </div>
        <p className="text-slate-600 text-xs font-mono">{t("brand_foot")}</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2 text-slate-900">
              <Receipt size={24} weight="bold" />
              <span className="font-heading font-bold tracking-tight">Pellku</span>
            </div>
            <div className="hidden lg:block" />
            <LangToggle />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{mode === "login" ? t("auth_signin") : mode === "register" ? t("auth_signup") : t("auth_reset")}</h2>
            <p className="text-sm text-slate-500 mt-1">{mode === "login" ? t("auth_signin_sub") : mode === "register" ? t("auth_signup_sub") : t("auth_forgot_sub")}</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Input data-testid="name-input" required placeholder={t("fullName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-11" />
            )}
            <Input data-testid="email-input" required type="email" placeholder={t("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-11" />
            {mode !== "forgot" && (
              <Input data-testid="password-input" required type="password" placeholder={t("password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none h-11" />
            )}
            <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-none bg-slate-900 hover:bg-slate-700 transition-colors duration-150">
              {busy ? t("pleaseWait") : mode === "login" ? t("auth_signin") : mode === "register" ? t("auth_signup") : t("sendResetLink")}
            </Button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setResetLink(""); }} className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors duration-150">
                {t("forgotPassword")}
              </button>
            )}
            {mode === "forgot" && (
              <button type="button" data-testid="back-to-login-link" onClick={() => setMode("login")} className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors duration-150">
                {t("backToSignIn")}
              </button>
            )}
          </form>

          {emailSent && (
            <div className="border border-emerald-200 bg-emerald-50 p-4" data-testid="email-sent-box">
              <p className="text-sm text-emerald-800">{t("emailSentBox")}</p>
            </div>
          )}

          {resetLink && (
            <div className="border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="reset-link-box">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("resetLinkLabel")}</p>
              <a href={resetLink} data-testid="reset-link-anchor" className="text-xs font-mono text-blue-700 underline break-all">{resetLink}</a>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{t("or")}</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <Button data-testid="google-login-btn" onClick={googleLogin} variant="outline" className="w-full h-11 rounded-none border-slate-300">
            <GoogleLogo size={18} weight="bold" className="mr-2" /> {t("continueGoogle")}
          </Button>

          <p className="text-sm text-slate-500 text-center">
            {mode === "login" ? t("noAccount") : t("haveAccount")}{" "}
            <button data-testid="toggle-auth-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-slate-900 underline underline-offset-4">
              {mode === "login" ? t("signUp") : t("auth_signin")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
